import React, { CSSProperties, useState } from 'react'
import Icon from '@embracesbs/component-icon'

interface AddCardProps {
  label: string
  onClick: () => void
  variant?: 'ghost' | 'solid'
  style?: CSSProperties
}

const baseStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  boxSizing: 'border-box',
  width: '100%',
  borderRadius: '8px',
  padding: '12px',
  margin: '0 0 6px 0',
  cursor: 'pointer',
  fontWeight: 600,
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSize: '14px',
  border: '1px solid transparent',
  transition: 'background 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease'
}

// A "mock card" add affordance that sits with the list items.
// - ghost: lightweight dashed card (contextual, e.g. Add tenant)
// - solid: elevated, professional primary CTA (e.g. the pinned Add subscription)
const AddCard: React.FC<AddCardProps> = ({ label, onClick, variant = 'ghost', style }) => {
  const [isHovered, setIsHovered] = useState(false)

  const ghostStyle: CSSProperties = {
    border: '1px dashed ' + (isHovered ? '#2f56c8' : '#9db4f5'),
    background: isHovered ? 'rgba(65, 110, 236, 0.06)' : 'transparent',
    color: 'var(--tma-accent)'
  }

  const solidStyle: CSSProperties = {
    border: 'none',
    padding: '14px',
    background: isHovered ? '#3559c9' : '#416eec',
    color: '#ffffff',
    boxShadow: isHovered ? '0 6px 16px rgba(65, 110, 236, 0.35)' : '0 2px 6px rgba(65, 110, 236, 0.25)',
    transform: isHovered ? 'translateY(-1px)' : 'none'
  }

  const variantStyle = variant === 'solid' ? solidStyle : ghostStyle
  const iconColor = variant === 'solid' ? '#ffffff' : '#416eec'

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ ...baseStyle, ...variantStyle, ...style }}
    >
      <Icon iconName='Add' size={16} color={iconColor} />
      <span>{label}</span>
    </div>
  )
}

export default AddCard
